// The portal shell from the mock: the PHONEWARE wordmark, the grouped sidebar
// (a dealer's, or the Phoneware side's for every admin tier, mock M1), the context
// bar with the account menu, the act-as banner while a Super admin acts as
// someone (issue #1771), and the footer.
import React, { useEffect, useRef, useState } from "react";
import { roleLabel, type ActingAsView, type Me, type VersionInfo } from "@phoneware/lens-domain";
import { Icon, type IconName } from "./icons.js";
import { Link, navigate, useLocation } from "./router.js";
import { useLoad, useSession, viewerKind, type ViewerKind } from "./session.js";
import { errorText, firstName, initials } from "./format.js";
import { Wordmark, useToast } from "./ui.js";
import { useBillingChanges } from "./billing-changes.js";
import { Appearance } from "./appearance.js";
import { OpenRequestContext, RequestComposer } from "./screens/RequestComposer.js";
import { RequestBatchProvider, type ComposeMode } from "./screens/request-batch.js";

export type NavId = "today" | "quotes" | "orders" | "customers" | "catalog" | "requests" | "settings" | "approvals" | "dealers";

interface NavItem {
  id: NavId;
  label: string;
  to: string;
  icon: IconName;
}

const QUOTES: NavItem = { id: "quotes", label: "Quotes", to: "/quotes", icon: "file-text" };
const ORDERS: NavItem = { id: "orders", label: "Orders", to: "/orders", icon: "package" };
const CUSTOMERS: NavItem = { id: "customers", label: "Customers", to: "/customers", icon: "building-2" };
const CATALOG: NavItem = { id: "catalog", label: "Catalog", to: "/catalog", icon: "book-open" };
const REQUESTS: NavItem = { id: "requests", label: "Requests", to: "/requests", icon: "message-square-text" };
const SETTINGS: NavItem = { id: "settings", label: "Settings", to: "/settings", icon: "settings" };

function navGroups(kind: ViewerKind, seesPrices: boolean): Array<[string, NavItem[]]> {
  // Someone who doesn't see prices (a dealer's tech) gets customers and orders, so no quotes and no catalog.
  const selling = seesPrices ? [QUOTES, ORDERS, CUSTOMERS, CATALOG] : [ORDERS, CUSTOMERS];
  switch (kind) {
    case "admin":
      return [
        [
          "Phoneware",
          [
            { id: "today", label: "Home", to: "/", icon: "gauge" },
            { id: "approvals", label: "Approvals", to: "/approvals", icon: "badge-check" },
            { id: "dealers", label: "Dealers", to: "/dealers", icon: "store" },
          ],
        ],
        ["Selling", selling],
        ["Omni", [REQUESTS, SETTINGS]],
      ];
    case "dealer":
      return [
        ["Overview", [{ id: "today", label: "Today", to: "/", icon: "house" }]],
        ["Selling", selling],
        ["Dealer", [REQUESTS, SETTINGS]],
      ];
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
}

/** Stops acting and says so; the session already sent the admin back to the dealer. */
function useStop(): { stopping: boolean; stop: () => void } {
  const { stopActing } = useSession();
  const toast = useToast();
  const [stopping, setStopping] = useState(false);
  return {
    stopping,
    stop: () => {
      setStopping(true);
      stopActing().then(
        (line) => {
          if (line) toast(line);
        },
        (err: unknown) => {
          setStopping(false);
          toast(`Omni did not stop acting: ${errorText(err)}`);
        },
      );
    },
  };
}

/** Under the top bar on every screen while acting, never scrolled away (mock M1 states 53 to 57). */
function ActAsBanner({ acting }: { acting: ActingAsView }): React.ReactElement {
  const { stopping, stop } = useStop();
  const t = acting.target;
  return (
    <div className="actas" role="status" data-testid="act-as-banner">
      <span className="as-ic" aria-hidden="true">
        <Icon name="user" size={15} />
      </span>
      <p data-testid="act-as-text">
        Acting as <b>{t.name}</b> ({roleLabel(t.role)}, {t.dealerName}). Changes are recorded as {acting.admin.name} acting as {firstName(t.name)}.
      </p>
      <button className="btn small" type="button" data-testid="act-as-stop" disabled={stopping} onClick={stop}>
        <Icon name="x" size={14} />
        {stopping ? "Stopping" : "Stop"}
      </button>
    </div>
  );
}

function AccountMenu(): React.ReactElement {
  const { me, signOut } = useSession();
  const { stopping, stop } = useStop();
  const acting = me.actingAs ?? null;
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && e.target instanceof Node && !box.current.contains(e.target)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);
  return (
    <div className="acct" ref={box}>
      <button className="acct-btn" type="button" aria-haspopup="menu" aria-expanded={open} aria-label={`${initials(me.user.name)}, your account`} onClick={() => setOpen(!open)}>
        <span className={acting ? "avatar as" : "avatar"} title={me.user.name}>
          {initials(me.user.name)}
        </span>
      </button>
      {open ? (
        <div className="acct-menu" role="menu">
          {acting ? (
            <>
              <span className="pm-who">
                <b>{acting.admin.name}</b>
                <span>{acting.admin.email}</span>
                <span>{roleLabel(acting.admin.role)}</span>
              </span>
              <hr />
              <span className="pm-who pm-as">
                <b>Acting as {acting.target.name}</b>
                <span>
                  {roleLabel(acting.target.role)}, {acting.target.dealerName}
                </span>
              </span>
              <button type="button" role="menuitem" disabled={stopping} onClick={stop}>
                <Icon name="x" size={15} />
                Stop acting as {firstName(acting.target.name)}
              </button>
            </>
          ) : (
            <span className="pm-who">
              <b>{me.user.name}</b>
              <span>{me.user.email}</span>
              <span>
                {roleLabel(me.user.role)}
                {me.dealer ? `, ${me.dealer.name}` : ""}
              </span>
            </span>
          )}
          {acting ? null : (
            <button
              type="button"
              role="menuitem"
              data-testid="menu-signin"
              onClick={() => {
                setOpen(false);
                navigate("/settings/signin");
              }}
            >
              <Icon name="lock" size={15} />
              Sign-in
            </button>
          )}
          <hr />
          <button type="button" role="menuitem" data-testid="sign-out" onClick={signOut}>
            <Icon name="log-out" size={15} />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The context bar's left side and the sidebar's foot, for each kind of person. */
function placeOf(kind: ViewerKind, me: Me): { ctx: React.ReactNode; foot: string } {
  switch (kind) {
    case "admin":
      return {
        ctx: (
          <>
            <div className="ctx">
              <b data-testid="shell-dealer-name">Phoneware Operations</b>
              <span data-testid="shell-tier">{roleLabel(me.user.role)} · every dealer</span>
            </div>
            <span className="badge">Internal</span>
          </>
        ),
        foot: "Run by Phoneware",
      };
    case "dealer": {
      const dealer = me.dealer;
      return {
        ctx: (
          <div className="ctx">
            <b data-testid="shell-dealer-name">{dealer?.name ?? "Your dealer"}</b>
            <span>{dealer ? [dealer.city, dealer.model === "agent" ? "Dealer" : "Reseller"].filter(Boolean).join(" \u00b7 ") : "Dealer"}</span>
          </div>
        ),
        foot: `${dealer?.name ?? "Your dealer"} on Phoneware`,
      };
    }
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
}

export function Shell({ nav, children }: { nav: NavId; children: React.ReactNode }): React.ReactElement {
  const { me, api, can, everyDealer, seesPrices } = useSession();
  const { path } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  // The composer opens for one request (header, Requests page) or for one screen of the batch (the dock).
  const [compose, setCompose] = useState<{ path: string; mode: ComposeMode } | null>(null);
  const kind = viewerKind(me.user);
  // Who decides what waits: a tier that only watches the queue gets a quiet count and no dot.
  const decides = can("approvals.decide") || can("billing.approve");
  const reviews = can("sync.run");
  // The approvals count beside the Phoneware nav: below-floor quotes and orders waiting to bill, read again whenever the
  // screen changes or billing is approved. Reading the queue is a permission: whoever reads every dealer.
  const billing = useBillingChanges();
  const waiting = useLoad(
    () => (everyDealer ? Promise.all([api.approvals(), api.ordersToBill()]).then(([quotes, orders]) => quotes.length + orders.length) : Promise.resolve(0)),
    [everyDealer, path, billing],
  );
  // What the customer sync could not settle on its own: categories that differ, customers waiting for a dealer, open OneBill accounts.
  const toReview = useLoad(
    () => (reviews ? api.syncStatus().then((s) => (s.customers ? s.customers.counts.differs + s.customers.counts.waiting : 0) + (s.billing?.counts.open ?? 0)) : Promise.resolve(0)),
    [reviews, path],
  );
  const version = useLoad<VersionInfo>(() => api.version(), []);

  useEffect(() => {
    document.body.classList.toggle("nav-open", navOpen);
    return () => document.body.classList.remove("nav-open");
  }, [navOpen]);
  useEffect(() => setNavOpen(false), [path]);

  const count = waiting.data ?? 0;
  const place = placeOf(kind, me);
  return (
    <div className="app">
      <aside className="side" aria-label="Omni">
        <Link className="wm-link" to="/" aria-label="Omni home">
          <Wordmark />
        </Link>
        <nav className="nav" aria-label="Main">
          {navGroups(kind, seesPrices).map(([group, items]) => (
            <React.Fragment key={group}>
              <p className="nav-g">{group}</p>
              {items.map((item) => (
                <Link key={item.id} to={item.to} data-testid={`nav-${item.id}`} aria-current={nav === item.id ? "page" : undefined}>
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                  {decides && item.id === "today" && count > 0 ? <i className="dot warn" title="Approvals waiting" /> : null}
                  {reviews && item.id === "customers" && (toReview.data ?? 0) > 0 ? <i className="dot warn" title="Customers to review" data-testid="nav-customers-review" /> : null}
                  {item.id === "approvals" && count > 0 ? (
                    <b className={decides ? "count" : "count quiet"} aria-label={`${count} waiting`}>
                      {count}
                    </b>
                  ) : null}
                </Link>
              ))}
            </React.Fragment>
          ))}
        </nav>
        <div className="side-foot">
          <b>Omni, the dealer portal</b>
          {place.foot}
        </div>
      </aside>
      <div className="navscrim" aria-hidden="true" onClick={() => setNavOpen(false)} />
      <div className="main">
        <header className="top">
          <button className="menubtn" type="button" aria-label="Menu" onClick={() => setNavOpen(!navOpen)}>
            <Icon name="menu" size={20} />
          </button>
          {place.ctx}
          <div className="top-r">
            {can("requests.create") ? (
              <button className="btn small global-request" type="button" aria-haspopup="dialog" aria-label="Request a change"
                data-testid="global-request" onClick={() => setCompose({ path: window.location.pathname, mode: "single" })}>
                <span className="request-face"><Icon name="message-square-text" size={14} /></span>
                <span className="action-text">Request<span className="action-tail"> a change</span></span>
              </button>
            ) : null}
            <Appearance />
            <AccountMenu />
          </div>
        </header>
        {me.actingAs ? <ActAsBanner acting={me.actingAs} /> : null}
        {/* The batch's dock and Review request render on the body, outside .app, so a capture never includes them. */}
        <RequestBatchProvider onAddPage={() => setCompose({ path: window.location.pathname, mode: "batch" })}>
          {compose ? <RequestComposer key={`${compose.mode}:${compose.path}`} path={compose.path} mode={compose.mode} onClose={() => setCompose(null)} /> : null}
        </RequestBatchProvider>
        <main className="view" tabIndex={-1}>
          <OpenRequestContext.Provider value={() => setCompose({ path: window.location.pathname, mode: "single" })}>{children}</OpenRequestContext.Provider>
          <footer className="foot">
            <span>Omni, the Phoneware dealer portal</span>
            {version.data ? <span>Version {version.data.sha.slice(0, 7)}</span> : null}
          </footer>
        </main>
      </div>
    </div>
  );
}
